import React, { useEffect } from 'react';
import { View, Modal } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useForm } from 'react-hook-form';
import { useIsFocused } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { accessService } from '../../../services/central-api/access';
import MyAppText from '../../../components/MyAppText';
import ActingBodiesDropdown from '../../../components/access/ActingBodiesDropdown';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
    actingBodyCommandId?: number;
    onClose: () => void;
}

interface ActingBodyCommandForm {
    actingBodyId: number;
    name: string;
}

export default function ActingBodyCommandModal({ actingBodyCommandId, isModalVisible, setModalVisible, onClose }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getActingBodyCommandModal', actingBodyCommandId, isModalVisible],
        queryFn: () => accessService.getActingBodyCommandById(actingBodyCommandId!),
        enabled: !!authenticatedUser && !!actingBodyCommandId && isModalVisible && isFocused,
        staleTime: 0,
    });

    const { data } = queryResult;

    const defaultEmptyValues: ActingBodyCommandForm = {
        actingBodyId: 0,
        name: '',
    };

    const form = useForm<ActingBodyCommandForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    useEffect(() => {
        if (data) {
            form.reset(data);
        }
    }, [data]);

    function clearValues() {
        setModalVisible(false);
        form.reset(defaultEmptyValues);
        onClose();
    }

    async function formSubmit() {
        try {
            const data = form.getValues();
            if (actingBodyCommandId) {
                await accessService.updateActingBodyCommand({
                    ...data,
                    id: actingBodyCommandId
                });
            } else {
                await accessService.createActingBodyCommand(data);
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            clearValues();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => clearValues()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile, 400, 300)}>
                    <View style={{ gap: 24 }}>
                        <View style={{ flexDirection: 'column', gap: 8 }}>
                            <MyAppText style={styles.headerText}>{translate(actingBodyCommandId ? 'updateActingBodyCommand' : 'createActingBodyCommand')}</MyAppText>
                        </View>
                        <View style={{ gap: 24 }}>
                            <ActingBodiesDropdown
                                includeAllOption={false}
                                form={form}
                                fieldName='actingBodyId'
                                zIndex={2}
                                required={true}
                                disabled={!!actingBodyCommandId}
                                labelFontSize={14}
                            />
                            <FormInput
                                label={translate('name')}
                                placeholder={translate('name')}
                                form={form}
                                fieldName='name'
                                required={true}
                                labelFontSize={14}
                            />
                        </View>
                    </View>
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => clearValues()}
                        disabled={!form.formState.isValid}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: (isMobile: boolean, width: number, minHeight: number) => {
        return {
            width: isMobile ? '95%' : width,
            minHeight: minHeight,
            backgroundColor: theme.colors.container.background,
            borderRadius: 8,
            padding: 32,
            rowGap: 24
        };
    },
    headerText: {
        fontSize: 18,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    }
}));
